/**
 * compile → replay (apply) → assemble, the change map for review, and the
 * payload a patched revision persists at issue.
 */
import { assembleDocument } from '../assemble/assemble.js';
import { compileComposition } from '../document/compose.js';
import { plainText, walkInlines } from '../ir/walk.js';
import { childLists } from './address.js';
import { applyPatches, inlinePlainText } from './apply.js';
import { foldCustomFields } from './fields.js';
import { isCustomFieldKey } from './types.js';
import { effectiveActionLog, replayLog } from './log.js';
import { DEVIATION_LABEL } from './types.js';
const isLog = (edits) => !Array.isArray(edits);
/** Collects the change map from a tracked document (`applyPatches(…, { track: true })`). */
export function collectChanges(tracked) {
    const out = [];
    const visit = (nodes) => {
        for (const node of nodes) {
            const edit = node.edit;
            const nodeAddress = edit?.address ?? (edit?.patchId ? `@${encodeURIComponent(edit.patchId)}` : '');
            const origin = { ...(edit?.layer ? { layer: edit.layer } : {}), ...(edit?.suggestion ? { suggestion: edit.suggestion } : {}) };
            if (edit?.movedBy) {
                out.push({ patchId: edit.movedBy, kind: 'inserted', moved: true, nodeAddress, nodeType: node.type, after: plainText([node]), ...origin });
            }
            if (edit?.change && edit.patchId) {
                const base = {
                    patchId: edit.patchId,
                    kind: edit.change,
                    nodeAddress,
                    nodeType: node.type,
                    ...(edit.moved && edit.change === 'removed' ? { moved: true } : {}),
                    ...origin,
                };
                if (edit.change === 'edited') {
                    if (node.type === 'section')
                        out.push({ ...base, before: edit.beforeTitle ?? '', after: node.title ?? '' });
                    else if (node.type === 'paragraph' || node.type === 'heading') {
                        out.push({ ...base, before: inlinePlainText(edit.before ?? []), after: inlinePlainText(node.content) });
                    }
                }
                else if (edit.change === 'removed') {
                    out.push({ ...base, before: plainText([node]) });
                    continue;
                }
                else {
                    out.push({ ...base, after: plainText([node]) });
                    continue;
                }
            }
            for (const { list } of childLists(node))
                visit(list);
        }
    };
    for (const part of tracked.parts) {
        visit(part.letterhead ?? []);
        visit(part.body);
        visit(part.footer ?? []);
    }
    return out;
}
/**
 * The change map of `edits` over a compiled base — what the review panel
 * lists ("§ 4 ändrad", "Stycke borttaget"), in document order.
 */
export function diffPatched(base, edits) {
    const tracked = isLog(edits) ? replayLog(base, edits, { track: true }) : applyPatches(base, edits, { track: true });
    return collectChanges(tracked.document);
}
const unresolvedMessage = (ref) => `Reference to unknown section "${ref}".`;
/** Turns references to removed sections into explicit diagnostics. */
function danglingDiagnostics(document, removed, diagnostics) {
    if (removed.length === 0)
        return diagnostics;
    const byRef = new Map(removed.map((r) => [r.ref, r.patchId]));
    const dangling = new Map();
    for (const part of document.parts) {
        for (const list of [part.letterhead ?? [], part.body, part.footer ?? []]) {
            walkInlines(list, (n) => {
                if (n.type === 'sectionRef' && byRef.has(n.target))
                    dangling.set(n.target, (dangling.get(n.target) ?? 0) + 1);
            });
        }
    }
    const replaced = new Set([...dangling.keys()].map(unresolvedMessage));
    return [
        ...diagnostics.filter((d) => !(d.code === 'unresolved_section_ref' && replaced.has(d.message))),
        ...[...dangling].map(([ref, count]) => ({
            severity: 'error',
            code: 'dangling_section_ref',
            message: `${count} reference(s) point at section "${ref}", which edit "${byRef.get(ref)}" removed.`,
        })),
    ];
}
const apply = (base, edits, track, references, proposals = 'hide') => {
    const opts = { track, ...(references ? { references } : {}) };
    return isLog(edits) ? replayLog(base, edits, { ...opts, proposals }) : { ...applyPatches(base, edits, opts), report: undefined };
};
/**
 * compile → replay → assemble. Numbering and references are computed after
 * the edits, so removals renumber and inserted clauses number in place. A
 * conflict is an error diagnostic: a document with unresolved conflicts is
 * not `ok` and must not be issued.
 */
export function buildPatchedDocument(composition, registry, context, edits, options = {}) {
    const compiled = compileComposition(composition, registry, context, options);
    const references = {
        values: composition.values ?? {},
        format: context.format,
        ...options.references,
    };
    return assemblePatched(compiled.document, edits, { ...options, references }, compiled.diagnostics);
}
/**
 * replay → assemble over an already compiled (or stored) base — e.g. a
 * stored compiled revision's IR.
 */
export function assemblePatched(base, edits, options = {}, upstream = []) {
    const patched = apply(base, edits, false, options.references);
    const assembled = assembleDocument(patched.document, options);
    const label = (id) => (isLog(edits) ? `Edit #${id}` : `Patch "${id}"`);
    const diagnostics = [
        ...upstream,
        ...danglingDiagnostics(patched.document, patched.removedSections, assembled.diagnostics),
        ...patched.conflicts.map((c) => ({
            severity: 'error',
            code: 'patch_conflict',
            message: `${label(c.patchId)} (${c.op}) was not applied: ${c.reason}.`,
        })),
        ...patched.detachedValues.map((v) => ({
            severity: 'warning',
            code: v.reason === 'field_removed' ? 'patch_field_removed' : 'patch_value_detached',
            message: v.reason === 'field_removed'
                ? `${label(v.patchId)} references own field "${v.sourceKey ?? ''}", which is not defined; printed as fixed text.`
                : `${label(v.patchId)} turned a form value into fixed text.`,
            ...(v.sourceKey ? { slotKey: v.sourceKey } : {}),
        })),
    ];
    let tracked;
    let changes;
    if (options.track) {
        const trackedApply = apply(base, edits, true, options.references, options.proposals);
        tracked = assembleDocument(trackedApply.document, options).document;
        changes = collectChanges(trackedApply.document);
    }
    const { document: _d, report, ...summary } = patched;
    return {
        ...summary,
        document: assembled.document,
        base,
        diagnostics,
        ok: !diagnostics.some((d) => d.severity === 'error'),
        ...(report ? { report } : {}),
        ...(summary.deviatesFromStandard ? { deviationLabel: DEVIATION_LABEL } : {}),
        ...(tracked ? { tracked } : {}),
        ...(changes ? { changes } : {}),
    };
}
export const PATCHED_REVISION_SCHEMA = 'regta.document.patched';
export const PATCHED_REVISION_VERSION = 1;
export function patchedRevisionPayload(log, result, values) {
    const fields = foldCustomFields(log).fields;
    const customValues = values && fields.size > 0
        ? Object.fromEntries(Object.entries(values).filter(([k]) => isCustomFieldKey(k) && fields.has(k)))
        : undefined;
    return {
        schema: PATCHED_REVISION_SCHEMA,
        version: PATCHED_REVISION_VERSION,
        log,
        ...(result.report ? { effective: effectiveActionLog(log, result.report) } : {}),
        report: result.report ?? [],
        deviatesFromStandard: result.deviatesFromStandard,
        deviatingInstances: result.deviatingInstances,
        ...(customValues ? { customValues } : {}),
    };
}
/** True when the log has any entry that is not undone — i.e. the document is patched. */
export const hasEffectiveEdits = (report) => report.some((r) => r.status === 'applied' &&
    r.op !== 'undo' &&
    r.op !== 'rebase' &&
    r.op !== 'acceptSuggestion' &&
    r.op !== 'rejectSuggestion' &&
    r.fieldKey == null);
//# sourceMappingURL=build.js.map